import React, { useState } from 'react'
import useGetModelTC from '../hooks/useGetModelTC';
import Plot from 'react-plotly.js';
import { Button, Card, Loading, Option, Select, TextInput, Tooltip } from '@sede-enterprise/shell-ds-react-framework';
import useDebounce from '../../../util/useDebounce';
import useGetLiveModelTC from '../hooks/useGetLiveModelTC';
import { InfoCircle } from '@sede-enterprise/shell-ds-react-framework/build/esm/components/Icon/components';

const TopContributors = ({ modelDetails }) => {


    const layout = {
        margin: {
            l: 250,
            r: 80,
            b: 40,
            t: 10,
        },
        yaxis: {
            categoryorder: "trace",
            autorange: "reversed",
        },
        xaxis: {
            title: {
                text: "Average Contribution to Corrosion Rate Prediction in %",
            },
            titlefont: {
                size: 15,
            },
        },
    };

    const config = {
        // responsive: true,
        showLink: false,
        displayModeBar: false,
    };


    const [errorText, setErrorText] = useState(null);
    const [numberOfTopContributors, setNumberOfTopContributors] = useState(
        5,
        null
    );

    const debouncedNumberOfTopContributors = useDebounce(numberOfTopContributors, 500);

    // Assuming each bar requires 50px of vertical space and the base height is 300px
    const baseHeight = 200;
    const heightIncrement = 50;
    const totalHeight = +debouncedNumberOfTopContributors > 0
        ? baseHeight + (debouncedNumberOfTopContributors * heightIncrement)
        : undefined;

    const candidateSensors = modelDetails?.sensors?.filter(opt => opt.status === "OBSERVATION") || [];
    const [selectedSensor, setSelectedSensor] = useState(
        candidateSensors.length > 0 ? JSON.stringify(candidateSensors[0]) : undefined
    );

    const { data, count, loading } = useGetModelTC(JSON.parse(selectedSensor).id, modelDetails, debouncedNumberOfTopContributors);
    const { data: liveData, count: liveCount, loading: liveLoading } = useGetLiveModelTC(JSON.parse(selectedSensor).id, debouncedNumberOfTopContributors);

    const getTopContributor = (plotData) =>
        plotData && plotData[0] && plotData[0].y && plotData[0].y.length > 0
            ? plotData[0].y[0]
            : null;

    const topTrain = getTopContributor(data);
    const topLive = getTopContributor(liveData);

    return (
        <div>
            <h4>Top Contributors</h4>

            {!data && (loading || liveLoading) && <div className='loading-container' data-testid='loading'><Loading /></div>}
            {data &&
                <Card className='top-contributors-chart-card' bodyPadding={false}>
                    <div className='contributor-chart-controls'>
                        <div className='sensor-list-selection'>
                            <div className='label-header'>
                                <p className='data-label'>Select Sensor</p>
                                <Tooltip className='tooltip' placement='bottom' overlay={<div className='tooltip-content'>Observation Sensors available for comparison</div>} trigger={
                                    <Button variant='transparent' iconOnly icon={<InfoCircle />} />
                                } />
                            </div>
                            <Select value={selectedSensor} onChange={value => {
                                setSelectedSensor(value)
                            }
                            } allowClear={false} className='sensor-select'>
                                {modelDetails?.sensors?.map(opt => {

                                    return opt.status === "OBSERVATION" && <Option key={JSON.stringify(opt)}>
                                        {opt.id}
                                    </Option>
                                })}
                            </Select>
                        </div>
                        <div className='chart-count-wrapper'>
                            <p className='data-label'>Number of Top Contributors</p>
                            <TextInput placeholder='Enter number' value={numberOfTopContributors}
                                type='number'
                                onChange={(e) => {
                                    const value = e.target.value;
                                    const maxCount = Math.max(count, liveCount);
                                    setErrorText(null);
                                    if (value === '') {
                                        setErrorText("Number of contributors cannot be empty");
                                        setNumberOfTopContributors(value);
                                    } else if (value > 0 && value <= (maxCount || 100) && Number.isInteger(Number(value))) {
                                        setNumberOfTopContributors(value);
                                    }
                                    else if (value > (maxCount || 100)) {
                                        if (maxCount) {
                                            setErrorText("Number of contributors cannot exceed the total number of contributors " + maxCount);
                                        }
                                        else {
                                            setErrorText("Number of contributors cannot exceed 100");
                                        }
                                    }
                                }}
                            />
                            {errorText && <p className='data-label error-label'>{errorText}</p>}
                        </div>
                    </div>

                    {loading || liveLoading ? <div className='loading-container'><Loading /></div> :
                        <div className='tc-model-container'>
                            <br />
                            {topTrain && topLive && topTrain !== topLive && (
                                <div className="top-contributor-diff-msg" style={{ color: "#d97706", marginBottom: 12 }}>
                                    <strong>Note:</strong> <span>{topTrain}</span> is the top contributor in <b>Train</b> whereas <span>{topLive}</span> is the top contributor in <b>Live</b>.
                                </div>
                            )}

                            <div className='top-contributors-chart-plot model-tc-plot'>
                                <Plot
                                    data={data}
                                    layout={{
                                        ...layout,
                                        xaxis: {
                                            title: {
                                                text: "Avg Contribution to CR Prediction in % (Train Model)"
                                            },
                                        }
                                    }}
                                    config={config}
                                    useResizeHandler={true}
                                    style={{ width: "50%", height: `${totalHeight}px` }}
                                />
                                <Plot
                                    data={liveData}
                                    layout={{
                                        ...layout,
                                        xaxis: {
                                            title: {
                                                text: "Avg Contribution to CR Prediction in % (Live Model)"
                                            },
                                        }
                                    }}
                                    config={config}
                                    useResizeHandler={true}
                                    style={{ width: "50%", height: `${totalHeight}px` }}
                                />
                            </div>
                        </div>
                    }
                </Card>
            }

        </div>
    )
}

export default TopContributors